<!-- *************************************************************************
	TEMPLATE
	************************************************************************* -->

<template>
	<div class="container">
		<BRow>
			<BCol class="breadcrumbPadding" lg="4" md="1">
				<BBreadcrumb>
					<BBreadcrumbItem :to="{ name: 'home' }" :data-test-automation-id="getAutomationId('homeLink')">{{
						$t('homeLink')
					}}</BBreadcrumbItem>
				</BBreadcrumb>
			</BCol>
			<BCol class="breadcrumbPadding" lg="8" md="11">
				<BRow>
					<BCol class="signedInUci" lg="8" md="8">
						<p>{{ $t('signedInUciLink') }} {{ uciNumber }}</p>
					</BCol>
					<BCol class="breadcrumbLinksRight" lg="4" md="4">
						<router-link
							:to="{ name: 'user-preference' }"
							:data-test-automation-id="getAutomationId('accountProfileLink')"
							>{{ $t('accountProfileLink') }}</router-link
						>
						<span class="seperator"></span>
						<a href="#" @click="logout" :data-test-automation-id="getAutomationId('logoutLink')">{{
							$t('logoutLink')
						}}</a>
					</BCol>
				</BRow>
			</BCol>
		</BRow>
		<span aria-live="assertive" role="region">
			<section>
				<BRow class="warnNotification" v-show="displayLogoutNotif">
					<BCol>
						<h2 aria-live="assertive">{{ $t(notif) }}</h2>
						<p aria-live="assertive">
							{{ $t('notifications.logoutMessage1') }}
							<a
								class="notifLink"
								@click="signOut"
								:data-test-automation-id="getAutomationId('logoutNotifLink')"
								href="#"
							>
								{{ $t('notifications.logoutLink') }}
							</a>
						</p>
					</BCol>
				</BRow>
			</section>
		</span>
		<BRow class="pb-2">
			<BCol lg="4" md="auto">
				<img src="../../assets/success-img.png" />
			</BCol>
			<BCol lg="8" md="auto" class="pt-5">
				<h2>{{ $t('validateCompleteTitle') }}</h2>
				<p class="text_font">{{ $t('verifyMessage') }}</p>
			</BCol>
		</BRow>
		<BRow class="bottomRowPadding">
			<BCol lg="6" md="12">
				<h2>{{ $t('homeTitle') }}</h2>
				<p class="text_font">{{ $t('homeMessage') }}</p>
				<BaseButton
					class="navButton"
					:id="getUniqueId('continueButtonHome')"
					@click="goToLandingPage"
					:label="$t('continueButtonHome')"
					:aria-label="$t('continueButtonHome')"
					:automation-id="getAutomationId('continueButtonHome')"
					:pill="false"
					variant="up-secondary"
				/>
			</BCol>
			<BCol lg="6" md="12">
				<h2>{{ $t('accountProfileTitle') }}</h2>
				<p class="text_font">{{ $t('profileMessage') }}</p>
				<BaseButton
					class="navButton"
					:id="getUniqueId('continueButtonAccountProfile')"
					@click="goToUserPreferencesPage"
					:label="$t('continueButtonAccountProfile')"
					:aria-label="$t('continueButtonAccountProfile')"
					:automation-id="getAutomationId('continueButtonAccountProfile')"
					:pill="false"
					variant="up-secondary"
				/>
			</BCol>
		</BRow>
		<BRow class="bottomRowPadding"> </BRow>
	</div>
</template>
<!-- *************************************************************************
	SCRIPT
	************************************************************************* -->

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import IdMixin from '@/mixins/id';
import TabTrackerMixin from '@/mixins/tabs';
import BaseButton from '@/components/common/base/BaseButton.vue';
import BaseForm from '@/components/common/base/BaseForm.vue';
import { BRow, BCol, BCard, BFormRow } from 'bootstrap-vue';

@Component({
	name: 'ValidateComplete',
	components: {
		BaseButton,
		BaseForm,
		BRow,
		BCol,
		BCard,
		BFormRow
	},
	props: {
		automationId: {
			type: String,
			required: true
		}
	},
	mixins: [IdMixin, TabTrackerMixin],
	beforeRouteEnter(to, from, next) {
		if (from.name != 'validate') {
			next(vm => {
				vm.$router.push('home');
			});
		} else {
			next();
		}
	}
})
export default class ValidationComplete extends Vue {
	uciNumber = this.$store.state.auth.uci;
	displayLogoutNotif = false;

	goToLandingPage() {
		this.$router.push({ name: 'home' });
	}

	goToUserPreferencesPage() {
		this.$router.push({ name: 'user-preference' });
	}

	logout() {
		this.displayLogoutNotif = true;
		this.notif = 'notifications.logout';
	}

	signOut() {
		if (this.$store.state.auth.authenticated) {
			this.$store.dispatch('signOut');
			this.closeTabs();
		}
		this.$router.push('sign-in');
	}
}
</script>
<style lang="scss" @scoped>
.container > div {
	margin-bottom: 1%;
	margin-left: 0;
	margin-right: 0;
}

.breadcrumb {
	text-align: left;
	background-color: #ffffff;
	padding-left: 0;
	@media (max-width: 768px) {
		padding-bottom: 10px;
		margin-bottom: 0px;
	}
}

.breadcrumb a {
	color: #284162;
	@media (max-width: 768px) {
		font: normal normal normal 14px/19px Noto Sans;
	}
	@media (min-width: 768px) {
		font: normal normal normal 16px/22px Noto Sans;
	}
}

.breadcrumbLinksRight {
	padding-top: 12px;
	padding-right: 16px;
	a {
		color: #284162;
		padding-bottom: 12px;
		padding-right: 0;
		padding-left: 0;
	}
	@media (max-width: 768px) {
		text-align: left;
		padding-left: 15px;
		a {
			font: normal normal normal 14px/19px Noto Sans;
		}
	}
	@media (min-width: 768px) {
		padding-left: 0;
		text-align: right;
		a {
			font: normal normal normal 16px/22px Noto Sans;
		}
	}
}

.signedInUci {
	p {
		color: #284162;
		padding-right: 0;
	}
	@media (max-width: 768px) {
		padding-left: 15px;
		padding-right: 0;
		padding-top: 0;
		padding-bottom: 0;
		text-align: left;
		p {
			font: normal normal normal 14px/19px Noto Sans;
			padding-bottom: 0;
			margin-bottom: 0;
		}
	}
	@media (min-width: 768px) {
		padding-left: 0;
		padding-right: 30px;
		padding-top: 12px;
		text-align: right;
		p {
			font: normal normal normal 16px/22px Noto Sans;
			padding-bottom: 12px;
		}
	}
}

.breadcrumbLinksRight .seperator::after {
	font-family: 'Noto', sans-serif;
	padding: 0px 10px;
	color: #284162;
	content: '|';
}

.breadcrumbPadding {
	padding-right: 0;
	padding-left: 0;
}

.navButton {
	float: right;
	max-width: 250px;
}

.topRowPadding {
	padding-bottom: 100px;
}

.bottomRowPadding {
	padding-bottom: 100px;
}
h2 {
	text-align: left;
	font: normal normal bold 27px/33px Lato;
	letter-spacing: 0px;
	color: #333333;
	opacity: 1;
}
.warnNotification {
	background: #ffffff 0% 0% no-repeat padding-box;
	min-height: 118px;
	padding-left: 1%;
	padding-bottom: 80px;
	h2 {
		text-align: left;
		font: normal normal bold 26px/32px Lato;
		letter-spacing: 0px;
		padding-left: 35px;
		padding-top: 20px;
		color: #000000;
		opacity: 1;
	}
	p {
		text-align: left;
		font: normal normal normal 20px/26px Noto Sans;
		letter-spacing: 0px;
		padding-left: 35px;
		color: #000000;
		opacity: 1;
	}
}

.warnNotification::before {
	font-family: 'Glyphicons Halflings';
	font-size: 26px;
	line-height: 2.3em;
	padding: 2px;
	position: absolute;
	color: #ee7120;
	content: url('../../assets/warning-icon.png');
}

.notifLink,
.notifLink:hover {
	text-align: left;
	text-decoration: underline;
	font: normal normal bold 20px/26px Noto Sans;
	letter-spacing: 0px;
	color: #295376;
}

.text_font {
	text-align: left;
	font: normal normal normal 18px/24px Noto Sans;
	letter-spacing: 0px;
	color: #707070;
	opacity: 1;
}
</style>
<!-- *************************************************************************
	TRANSLATIONS
	************************************************************************* -->

<i18n>
{
	"en": {
		"validateCompleteTitle": "Your profile has been successfully verified",
		"homeLink": "Home",
		"signedInUciLink": "Signed In UCI Number",
		"accountProfileLink": "Account Profile",
		"logoutLink": "Logout",
		"homeTitle": "Home",
		"continueButtonHome": "Continue",
		"continueButtonAccountProfile": "Continue",
		"accountProfileTitle": "Account Profile",
		"verifyMessage": "Thank you for registering. You may now use the Beneficiary Portal.",
		"homeMessage": "Continue to the home page",
		"profileMessage": "Continue to your account profile",
		"notifications": {
			"logout": "Are you sure you want to log out?", 
			"logoutMessage1": "Are you sure you want to logout? Please confirm and ",
			"logoutLink": "click here to logout."
		}
		
	},
	"fr": {
		"validateCompleteTitle": "Votre profil a été vérifié avec succès",
		"homeLink": "Accueil",
		"signedInUciLink": "Session ouverte Numéro IUC",
		"accountProfileLink": "Profil du compte",
		"logoutLink": "Fermer la session",
		"homeTitle": "Accueil",
		"continueButtonHome": "Continuer",
		"continueButtonAccountProfile": "Continuer",
		"accountProfileTitle": "Profil du compte",
		"verifyMessage": "Merci de votre inscription. Vous pouvez maintenant utiliser le Portail des bénéficiaires.",
		"homeMessage": "Continuer vers la page d’accueil ",
		"profileMessage": "Continuer vers votre profil de compte",
		"notifications": {
			"logout": "Voulez-vous vraiment fermer votre session?", 
			"logoutMessage1": "Are you sure you want to logout? Please confirm and ",
			"logoutLink": "cliquez ici pour vous déconnecter."
		}
	}
}
</i18n>
